/**
 * Notification sound helpers for the shared ops inbox.
 * Browser autoplay requires a prior user gesture to unlock audio.
 */

const SOUND_MUTE_KEY = "bubakii.notifications.soundMuted";
const SOUND_SRC = "/sounds/notification.wav";

/** Poll interval for unread detection (technical default; not business config). */
export const NOTIFICATION_POLL_INTERVAL_MS = 20000;

let audioEl = null;
let unlocked = false;

const getAudio = () => {
  if (!audioEl) {
    audioEl = new Audio(SOUND_SRC);
    audioEl.preload = "auto";
    audioEl.volume = 0.55;
  }
  return audioEl;
};

export const isNotificationSoundMuted = () => {
  try {
    return localStorage.getItem(SOUND_MUTE_KEY) === "true";
  } catch {
    return false;
  }
};

export const setNotificationSoundMuted = (muted) => {
  try {
    localStorage.setItem(SOUND_MUTE_KEY, muted ? "true" : "false");
  } catch {
    // ignore storage failures
  }
};

/**
 * Unlock audio on first user gesture (required by browser autoplay policy).
 */
export const unlockNotificationSound = () => {
  if (unlocked || typeof window === "undefined") return;
  const audio = getAudio();
  const prevVolume = audio.volume;
  audio.volume = 0;
  const playAttempt = audio.play();
  if (playAttempt?.then) {
    playAttempt
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = prevVolume;
        unlocked = true;
      })
      .catch(() => {
        audio.volume = prevVolume;
      });
  } else {
    audio.volume = prevVolume;
  }
};

export const playNotificationSound = async () => {
  if (typeof window === "undefined") return;
  if (isNotificationSoundMuted()) return;

  const audio = getAudio();
  try {
    audio.currentTime = 0;
    await audio.play();
    unlocked = true;
  } catch {
    // Autoplay blocked until unlockNotificationSound runs after a gesture
  }
};

/**
 * Attach one-time gesture listeners to unlock audio for the session.
 */
export const attachNotificationSoundUnlock = () => {
  if (typeof window === "undefined") return () => {};

  const unlock = () => {
    unlockNotificationSound();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };

  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });

  return () => {
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
};
